/**
 * Shared NET cell for the opener / unwinder / positions tables.
 *
 * Renders the signed `total YES − total NO` exposure (positive = excess YES)
 * off the live engine's `believedPosition`. Green when hedged, red when the
 * signed net exceeds the tolerance (`isUnhedged`). The tooltip breaks the net
 * out into Kalshi net / Poly YES / Poly NO.
 *
 * When no live `believedPosition` is present yet, it falls back to the slower
 * REST position group (per-side `contracts`) and renders the same signed net
 * in a neutral grey — or `—` when neither is available.
 */
import { Box, Tooltip } from '@mui/material';
import type {
  BelievedPosition,
  ReadPairVenuePositions,
  RuleCost,
} from '../types/entry.types';
import { IMBALANCE_TOLERANCE, isUnhedged, netBreakdown, signedNet } from './unhedged';
import {
  accountsLabel,
  restStaleVenueNote,
  venueSignedNet,
  type PairVenueView,
  type RowAccounts,
  type VenueUnavailableReason,
  type VenueViewStatus,
} from './venuePositions';

/**
 * WP-G parity flag. Default OFF (REST-authoritative): the ACT column keeps
 * reading the 10s REST positions poll until an operator confirms the engine net
 * matches in prod, then opts IN with `VITE_ACT_FROM_ENGINE=true`. When ON, the
 * ACT column reads the engine-pushed live net (`ruleCost.kalshiNet` / `polyNet`)
 * off the socket instead. Opt-in (not opt-out) also de-risks the prior-session
 * net-0 parity gap until reconcile parity is proven.
 */
export const ACT_FROM_ENGINE = import.meta.env.VITE_ACT_FROM_ENGINE === 'true';

/**
 * A `ruleCost` whose `computedAt` is older than this is treated as no longer
 * live (the rule/group stopped streaming, e.g. it finished or the pod stopped
 * emitting) → the ACT cell falls back to the REST-held net rather than freezing
 * a stale engine figure (incl. a stale red "naked" value after a position
 * closed). Mirrors `GroupLiveSummaryStrip`'s staleness window.
 */
export const ACT_STALE_MS = 15_000;

const HEDGED_COLOR = '#51cf66';
const UNHEDGED_COLOR = '#ff6b6b';
const REST_COLOR = 'grey.500';
export const VENUE_WARN_COLOR = '#ffa94d';

/**
 * Colour for a cell that has no number to show, by how bad the reason is. Only
 * a genuine failure is red; `starting` and `stale` are expected transients and
 * take the warn colour.
 */
export const venueUnavailableColor = (
  reason: VenueUnavailableReason
): string =>
  reason === 'blocked' || reason === 'error' ? UNHEDGED_COLOR : VENUE_WARN_COLOR;

/**
 * The venue endpoints are ACCOUNT-wide per market: another group, the unwinder
 * or a manual order on the same market is included in these contracts. That is
 * the intended reading (the operator wants the account total) — spelled out so
 * it cannot be mistaken for this rule's own position.
 */
/**
 * Said on every cell rendering HELD counts: they are this group's own numbers
 * (never a stand-in from another account), but they are not a current read —
 * the page lost the socket that carries the feed pod's freshness heartbeat.
 */
export const venueHeldNote = (lastSuccessLabel: string | null): string =>
  'LIVE LINK DOWN — this page lost its socket to the group’s feed pod, so ' +
  'these are the last counts it sent (last success ' +
  `${lastSuccessLabel ?? 'unknown'}), held while it reconnects. They are ` +
  'this group’s own account, not a stand-in, but they are not a current read.';

export const VENUE_ACCOUNT_WIDE_NOTE =
  'ACCOUNT-WIDE for this market, not rule-scoped: another group, the unwinder ' +
  'or a manual order on the same market is included.';

/**
 * Why a `resolving` pair's imbalance is not a naked leg. Shared by the ACT
 * cell and the YES / NO counts so all three describe the row the same way.
 */
export const VENUE_RESOLVING_NOTE =
  'RESOLVING: a leg already settled at the venue, so this difference is a ' +
  'resolution artefact pending redemption, not a naked leg.';

/** A REST position group with the per-side contract counts NetCell needs. */
export interface RestNetSource {
  positions?: { platform: 'polymarket' | 'kalshi'; side: 'yes' | 'no'; contracts: number }[];
}

const signed = (n: number): string =>
  `${n > 0 ? '+' : ''}${Number.isInteger(n) ? n : n.toFixed(2)}`;

/**
 * Signed `YES − NO` from a REST position group. Polymarket YES + Kalshi YES
 * are positive; Polymarket NO + Kalshi NO are negative. Mirrors the
 * believed-position sign convention (excess-YES positive).
 */
export const restSignedNet = (rest: RestNetSource): number => {
  let net = 0;
  for (const p of rest.positions ?? []) {
    net += p.side === 'yes' ? p.contracts : -p.contracts;
  }
  return net;
};

/**
 * Sign-agnostic size of the actual leg imbalance — `|total YES − total NO|`.
 * This is the number of contracts the operator must still hedge; the magnitude
 * is identical whether the excess sits on YES or NO, so the opener/unwinder
 * tables sort by this absolute value to surface the largest naked positions
 * first regardless of direction.
 */
export const restUnhedgeMagnitude = (rest: RestNetSource): number =>
  Math.abs(restSignedNet(rest));

/**
 * Said on a cell whose pair the backend refused to attribute to a venue-account
 * pair. The legs exist; whose book they sit in does not.
 */
export const NET_ACCOUNTS_UNRESOLVED_NOTE =
  'This pair’s venue accounts could not be resolved, so its legs were never ' +
  'attributed to an account. The net is UNKNOWN — which is not the same as flat.';

interface NetCellProps {
  bp: BelievedPosition | null | undefined;
  rest?: RestNetSource;
  /**
   * The backend nulled this pair's `netContracts` because it could not resolve
   * both venue accounts. The REST fallback below sums the SAME legs from the
   * account-blind positions map, so without this the cell would restate — in
   * confident colour, one column away from the `n/d net` that admits it — a
   * number the backend explicitly declined to compute. The believed position
   * is unaffected: it is the engine's own book for a known rule, not an
   * attribution of REST legs.
   */
  accountsUnresolved?: boolean;
}

export const NetCell = ({ bp, rest, accountsUnresolved }: NetCellProps) => {
  if (bp) {
    const net = signedNet(bp);
    const unhedged = isUnhedged(bp);
    const { kalshiNet, polyYes, polyNo } = netBreakdown(bp);
    const kLabel =
      kalshiNet >= 0 ? `+${kalshiNet} NO` : `+${Math.abs(kalshiNet)} YES`;
    const title = `K: ${kLabel} · P: YES ${polyYes} / NO ${polyNo}`;
    return (
      <Tooltip title={title} placement="top">
        <Box
          component="span"
          data-net-state={unhedged ? 'unhedged' : 'hedged'}
          data-net-title={title}
          sx={{
            color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {signed(net)}
        </Box>
      </Tooltip>
    );
  }

  if (accountsUnresolved) {
    return (
      <Tooltip title={NET_ACCOUNTS_UNRESOLVED_NOTE} placement="top">
        <Box
          component="span"
          data-net-state="accounts-unresolved"
          data-net-title={NET_ACCOUNTS_UNRESOLVED_NOTE}
          sx={{ color: VENUE_WARN_COLOR }}
        >
          n/d
        </Box>
      </Tooltip>
    );
  }

  if (rest && (rest.positions?.length ?? 0) > 0) {
    return (
      <Box
        component="span"
        data-net-state="rest"
        sx={{ color: REST_COLOR, fontVariantNumeric: 'tabular-nums' }}
      >
        {signed(restSignedNet(rest))}
      </Box>
    );
  }

  return (
    <Box component="span" data-net-state="empty" sx={{ color: REST_COLOR }}>
      —
    </Box>
  );
};

/**
 * REST "actual" NET cell — the signed `YES − NO` exposure read straight from
 * the held `prediction_positions`, independent of the live engine's belief.
 *
 * The live `NetCell` reads `believedPosition`, whose per-path counters only
 * tally contracts the engine filled THIS session — so a pre-existing position
 * (filled in a prior session / before a restart) reads net 0 there even when
 * the actual legs are imbalanced. This cell shows the real held imbalance, so
 * the operator can spot engine-belief-vs-reality drift at a glance. Coloured by
 * the same tolerance: green when hedged, red when the actual net is unhedged.
 */
export const REST_PROVENANCE_NOTE =
  'RESIDUAL-account held positions (the prediction_positions REST sync) — not ' +
  'a venue read. A group trading any other account is never shown here.';

/** The REST provenance note, naming the accounts these numbers belong to. */
export const restProvenanceNote = (status: VenueViewStatus): string =>
  `${REST_PROVENANCE_NOTE} These are ${accountsLabel(status.accounts)}.` +
  restStaleVenueNote(status);

export const RestNetCell = ({
  rest,
  provenanceNote,
}: {
  rest?: RestNetSource;
  /**
   * Opener-only. The opener table mixes this cell with venue-sourced ones, so
   * it needs the row to say WHICH account these numbers are — including on the
   * empty `—`, which is exactly where the operator would otherwise read
   * "flat". The unwinder tables have a single source and pass nothing, so they
   * keep their plain, hover-free empty cell.
   */
  provenanceNote?: string;
}) => {
  if (!rest || (rest.positions?.length ?? 0) === 0) {
    const empty = (
      <Box
        component="span"
        data-actnet-state="empty"
        data-actnet-source="rest"
        data-actnet-title={provenanceNote}
        sx={{ color: REST_COLOR }}
      >
        —
      </Box>
    );
    return provenanceNote ? (
      <Tooltip title={provenanceNote} placement="top">
        {empty}
      </Tooltip>
    ) : (
      empty
    );
  }
  const net = restSignedNet(rest);
  const unhedged = Math.abs(net) > IMBALANCE_TOLERANCE;
  const title =
    `Actual held position (total YES − total NO, from positions): ${signed(net)}` +
    (provenanceNote ? ` · ${provenanceNote}` : '');
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-actnet-state={unhedged ? 'unhedged' : 'hedged'}
        data-actnet-source="rest"
        data-actnet-title={title}
        sx={{
          color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {signed(net)}
      </Box>
    </Tooltip>
  );
};

/**
 * Venue "actual" NET cell — the signed `YES − NO` exposure the group's own feed
 * pod read straight off Kalshi + Polymarket under THIS group's account.
 *
 * Mirrors `RestNetCell`, but the number is account-correct: the REST
 * `prediction_positions` sync only covers the RESIDUAL account pair, so a group
 * on any other account reads 0/— there while actually holding contracts.
 *
 * The two accounts are reported SEPARATELY (`data-actnet-kalshi-account` /
 * `-poly-account`) because they are chosen separately: one attribute could not
 * describe a group holding Kalshi on one account and Polymarket on another.
 *
 * A `resolving` pair (one leg already settled at the venue) renders NEUTRAL,
 * never red: its imbalance is the settled leg's payout claim, not a naked leg,
 * and colouring it unhedged is what sends the operator hedging a position that
 * is only waiting to be redeemed.
 */
export const VenueNetCell = ({
  venue,
  accounts,
  resolving = false,
  redeemableExcluded = 0,
  held = false,
  lastSuccessLabel = null,
}: {
  // The narrowed type: this cell only ever renders a view whose
  // `kind === 'venue'`, which `resolvePairVenueView` produces only after
  // `countsUsable`. Taking the wide type here would let a NOT-READ Polymarket
  // leg be summed as zero and render a naked leg as balanced.
  venue: ReadPairVenuePositions;
  accounts: RowAccounts;
  resolving?: boolean;
  redeemableExcluded?: number;
  /** Counts held while the page has no socket — see VENUE_LINK_DOWN_NOTE. */
  held?: boolean;
  lastSuccessLabel?: string | null;
}) => {
  const net = venueSignedNet(venue);
  const unhedged = !resolving && Math.abs(net) > IMBALANCE_TOLERANCE;
  const title =
    `Venue position (${accountsLabel(accounts)}, total YES − total NO): ` +
    `${signed(net)}` +
    ` · P: YES ${venue.polyYes} / NO ${venue.polyNo}` +
    ` · K: YES ${venue.kalshiYes} / NO ${venue.kalshiNo}` +
    ` · ${VENUE_ACCOUNT_WIDE_NOTE}` +
    (resolving ? ` · ${VENUE_RESOLVING_NOTE}` : '') +
    (held ? ` · ${venueHeldNote(lastSuccessLabel)}` : '') +
    (redeemableExcluded > 0
      ? ` · ${redeemableExcluded} redeemable Polymarket row(s) excluded from` +
        ' this group’s counts (resolved, pending redemption)'
      : '');
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-actnet-state={
          resolving ? 'resolving' : unhedged ? 'unhedged' : 'hedged'
        }
        data-actnet-source="venue"
        data-actnet-kalshi-account={
          accounts.kind === 'resolved' ? accounts.kalshiAccountId : undefined
        }
        data-actnet-poly-account={
          accounts.kind === 'resolved'
            ? accounts.polymarketAccountId
            : undefined
        }
        data-actnet-held={held ? 'link-down' : undefined}
        data-actnet-title={title}
        sx={{
          // The hedged/unhedged colour SURVIVES the hold: a naked leg that
          // went amber because the socket dropped would read as an expected
          // transient. The hold is marked by the dotted rule + dimming.
          color: resolving
            ? VENUE_WARN_COLOR
            : unhedged
              ? UNHEDGED_COLOR
              : HEDGED_COLOR,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          ...(held
            ? {
                opacity: 0.6,
                borderBottom: `1px dotted ${VENUE_WARN_COLOR}`,
              }
            : {}),
        }}
      >
        {signed(net)}
      </Box>
    </Tooltip>
  );
};

export const VENUE_UNAVAILABLE_LABEL: Record<VenueUnavailableReason, string> = {
  unconfigured: 'no venue credentials configured for this group',
  blocked: 'venue reads blocked for this group',
  error: 'venue read failed',
  // Expected and transient — the pod is coming up, nothing has failed.
  starting:
    'the group’s feed pod is starting up and has not completed its first' +
    ' venue sweep yet',
  stale: 'venue read is stale',
  missing: 'the venue reported nothing for this market',
  ambiguous:
    'more than one pair in this group claims this market, so the venue' +
    ' contracts belong to no single row',
  'no-read':
    'no venue read for this group yet (this view is on the core gateway,' +
    ' which only sees the residual account pair, or this group’s accounts are' +
    ' not resolved)',
  // Reached only past VENUE_LINK_DOWN_HOLD_MS: while the hold is in force the
  // counts still render (marked), so this labels the moment they stop.
  'link-down':
    'this page has had no socket to the group’s feed pod for long enough' +
    ' that its last counts are no longer worth showing',
};

/**
 * The venue is the ONLY account-correct source for a group, so when it is
 * unavailable the cell shows `—`, never `0` and never the residual-account REST
 * number — either would read as "flat / hedged" for a group we cannot see.
 */
export const VenueUnavailableCell = ({
  reason,
  status,
}: {
  reason: VenueUnavailableReason;
  status: VenueViewStatus;
}) => {
  const color = venueUnavailableColor(reason);
  const title =
    `No venue position for ${accountsLabel(status.accounts)} — ` +
    `${VENUE_UNAVAILABLE_LABEL[reason]}. Not showing a number rather than a wrong one.` +
    (status.lastError ? ` · ${status.lastError}` : '') +
    (status.lastSuccessLabel
      ? ` · last success ${status.lastSuccessLabel}`
      : ' · never succeeded');
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-actnet-state="empty"
        data-actnet-source="venue-unavailable"
        data-actnet-reason={reason}
        data-actnet-title={title}
        sx={{ color, borderBottom: `1px dotted ${color}` }}
      >
        —
      </Box>
    </Tooltip>
  );
};

/**
 * Signed `YES − NO` net from the engine's live `ruleCost`. `polyNet` (`+` =
 * net-long YES) and `kalshiNet` (`+` = net-long NO) reuse the believed-position
 * sign convention, so total `YES − NO` exposure is `polyNet − kalshiNet` — the
 * same value `restSignedNet` derives from held contracts (parity).
 */
const ruleCostSignedNet = (
  rc: Pick<RuleCost, 'kalshiNet' | 'polyNet'>
): number => rc.polyNet - rc.kalshiNet;

/**
 * ACT ("actual net") cell. The source is decided ONCE per row by
 * `resolvePairVenueView` (shared with the YES / NO cells and the ACT sort) and
 * handed in as `view`; this component only renders it:
 *
 *  1. `venue` — account-correct, so it outranks everything.
 *  2. `unavailable` — `—` with the reason. Evaluated BEFORE the engine path:
 *     `ruleCost` is the engine's SESSION-scoped belief, so contracts filled
 *     before the last respawn read net 0 there, and a green `0` where the truth
 *     is unknown is the exact lie this cell exists to prevent.
 *  3. `rest` — a view proven to be on the RESIDUAL account pair (core gateway,
 *     a group on those same two accounts). The `VITE_ACT_FROM_ENGINE` opt-in
 *     reads the engine net here first, skipped once `computedAt` is older than
 *     `ACT_STALE_MS` so a rule that stopped streaming can't freeze its last net.
 */
export const ActNetCell = ({
  ruleCost,
  rest,
  view,
  status,
}: {
  ruleCost?: RuleCost | null;
  rest?: RestNetSource;
  view: PairVenueView;
  status: VenueViewStatus;
}) => {
  if (view.kind === 'venue') {
    return (
      <VenueNetCell
        venue={view.venue}
        accounts={view.accounts}
        resolving={view.resolving}
        redeemableExcluded={status.redeemableExcluded}
        held={status.reason === 'link-down'}
        lastSuccessLabel={status.lastSuccessLabel}
      />
    );
  }

  if (view.kind === 'unavailable') {
    return <VenueUnavailableCell reason={view.reason} status={status} />;
  }

  const fresh =
    !!ruleCost && Date.now() - ruleCost.computedAt <= ACT_STALE_MS;
  if (ACT_FROM_ENGINE && ruleCost && fresh) {
    const net = ruleCostSignedNet(ruleCost);
    const unhedged = Math.abs(net) > IMBALANCE_TOLERANCE;
    const title = `Engine live net (total YES − total NO): ${signed(net)} · K net ${signed(
      ruleCost.kalshiNet
    )} (+ = NO) · P net ${signed(ruleCost.polyNet)} (+ = YES)`;
    return (
      <Tooltip title={title} placement="top">
        <Box
          component="span"
          data-actnet-state={unhedged ? 'unhedged' : 'hedged'}
          data-actnet-source="engine"
          sx={{
            color: unhedged ? UNHEDGED_COLOR : HEDGED_COLOR,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {signed(net)}
        </Box>
      </Tooltip>
    );
  }

  return <RestNetCell rest={rest} provenanceNote={restProvenanceNote(status)} />;
};
